'use client';

import { useEffect, useState } from 'react';
import { AlertTriangle, Bug, Check, HelpCircle, Lightbulb, Loader2, RotateCw, Send } from 'lucide-react';
import { EMPTY_BODY_MESSAGE, SUPPORT_LIMITS, type SupportContext, type SupportKind } from '@/lib/schemas/support.schema';
import { buildContext } from '@/lib/support/context';
import { readEditorProbe } from '@/lib/support/editorProbe';
import { readContextSources } from '@/lib/support/environment';
import { buildSnapshot, projectBytes } from '@/lib/support/snapshot';
import { postForm } from '@/lib/support/send';
import { formatBytes } from '@/lib/support/labels';
import { EMPTY_DRAFT, useSupportPanel } from './SupportProvider';
import { appendShots, releaseShots, ShotsField } from './ShotsField';
import { AttachedContext } from './AttachedContext';

const KINDS: { kind: SupportKind; label: string; icon: typeof Bug }[] = [
  { kind: 'BUG', label: 'Un problème', icon: Bug },
  { kind: 'QUESTION', label: 'Une question', icon: HelpCircle },
  { kind: 'IDEA', label: 'Une idée', icon: Lightbulb },
];
const BODY_LABEL: Record<SupportKind, string> = { BUG: 'Que se passe-t-il ?', QUESTION: 'Votre question', IDEA: 'Votre idée' };
const PLACEHOLDER: Record<SupportKind, string> = {
  BUG: 'Décrivez ce qui se passe, et ce que vous attendiez…',
  QUESTION: 'Votre question…',
  IDEA: 'Votre idée, et ce qu\'elle vous permettrait de faire…',
};

type Sending =
  | { state: 'idle' }
  /** Ce qui part (« Captures et copie du projet »), et la progression reelle de l'envoi */
  | { state: 'sending'; what: string | null; loaded: number; total: number }
  /** R7 : la copie depasse 50 Mo */
  | { state: 'tooLarge' }
  | { state: 'sent'; number: number }
  /** L'envoi a echoue : la raison, entre parentheses dans le message */
  | { state: 'failed'; reason: string }
  /** Un refus a lire tel quel : la limite du jour (R8), ou une entree refusee */
  | { state: 'refused'; message: string };

const count = (n: number) => n.toLocaleString('fr-FR');
const PRIMARY = 'inline-flex items-center gap-1.5 rounded-[9px] bg-[var(--accent)] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[var(--accent-700)] disabled:cursor-not-allowed disabled:opacity-50';
const GHOST = 'rounded-[9px] px-3 py-2 text-xs font-semibold text-[var(--ink-600)] hover:bg-[var(--ink-100)]';

/** La nouvelle demande (P02) et son envoi (P05) */
export function NewRequestForm() {
  const { draft, setDraft, close, email, isOpen, setCapturing, open } = useSupportPanel();
  const [sending, setSending] = useState<Sending>({ state: 'idle' });
  const [emptyError, setEmptyError] = useState(false);
  // Rouvert apres un envoi reussi (quel que soit le bouton qui l'a ferme) : un formulaire vide
  useEffect(() => {
    if (isOpen) setSending(s => (s.state === 'sent' ? { state: 'idle' } : s));
  }, [isOpen]);

  const length = draft.body.trim().length;
  const tooLong = length > SUPPORT_LIMITS.bodyMax;
  const isBug = draft.kind === 'BUG';
  const [details, setDetails] = useState<{ context: SupportContext; copyBytes: number | null; hasCamera: boolean } | null>(null);
  // Le poids du projet ouvert, pour l'avertissement du projet lourd : mesure a l'ouverture d'un probleme
  // Relu toutes les deux secondes tant que le panneau est ouvert : le projet peut changer, ou se fermer
  const [projectName, setProjectName] = useState<string | null>(null);
  const [copyBytes, setCopyBytes] = useState<number | null>(null);
  useEffect(() => {
    if (!isOpen || !isBug) return;
    const measure = () => {
      const probe = readEditorProbe();
      setProjectName(probe?.project.name ?? null);
      setCopyBytes(probe ? projectBytes(probe.project) : null);
    };
    const first = setTimeout(measure, 0);
    const every = setInterval(measure, 2000);
    return () => { clearTimeout(first); clearInterval(every); };
  }, [isOpen, isBug]);
  // La copie trop lourde ne vaut que pour l'envoi qui l'a constatee : changer d'objet ou decocher l'efface
  useEffect(() => {
    setSending(s => (s.state === 'tooLarge' ? { state: 'idle' } : s));
  }, [draft.kind, draft.attachCopy]);
  const heavy = copyBytes !== null && copyBytes > SUPPORT_LIMITS.heavySnapshotBytes;

  const showDetails = () => {
    const probe = readEditorProbe();
    setDetails({
      context: buildContext('BUG', probe, readContextSources()),
      copyBytes: probe ? projectBytes(probe.project) : null,
      hasCamera: !!probe?.camera,
    });
  };

  const send = async (withoutCopy = false) => {
    if (length === 0) { setEmptyError(true); return; }
    if (tooLong) return;
    const probe = readEditorProbe();
    // L'envoi commence des maintenant : la compression d'un gros projet prend un instant
    setSending({ state: 'sending', what: null, loaded: 0, total: 0 });
    const form = new FormData();
    form.append('kind', draft.kind);
    form.append('body', draft.body);
    form.append('context', JSON.stringify(buildContext(draft.kind, probe, readContextSources())));
    appendShots(form, draft.shots);
    // A12 : la conversation avec l'assistant, quand on vient de lui (chantier ASI)
    if (draft.assistant?.length) form.append('assistant', JSON.stringify(draft.assistant));
    // R4, R5 : la copie du projet tel qu'il est affiche, pour un probleme seulement, si la case est cochee
    let withSnapshot = false;
    if (isBug && draft.attachCopy && probe && !withoutCopy) {
      const snapshot = await buildSnapshot(probe);
      if ('tooLarge' in snapshot) { setSending({ state: 'tooLarge' }); return; }
      form.append('snapshot', snapshot.blob, 'projet.archifacil.gz');
      form.append('snapshotRawBytes', String(snapshot.rawBytes));
      form.append('snapshotTakenAt', snapshot.takenAt);
      withSnapshot = true;
    }
    const what = [draft.shots.length ? 'Captures' : null, withSnapshot ? 'copie du projet' : null].filter(Boolean).join(' et ');
    setSending({ state: 'sending', what: what ? what[0].toUpperCase() + what.slice(1) : null, loaded: 0, total: 0 });
    const { status, data } = await postForm('/api/support/tickets', form, (loaded, total) =>
      setSending(s => (s.state === 'sending' ? { ...s, loaded, total } : s)));
    if (status >= 200 && status < 300 && data.number) {
      releaseShots(draft.shots);
      setDraft(EMPTY_DRAFT);
      setSending({ state: 'sent', number: data.number });
    } else if (status === 429 || status === 400 || status === 413) {
      setSending({ state: 'refused', message: data.error ?? 'La demande a été refusée.' });
    } else {
      setSending({ state: 'failed', reason: status === 0 ? 'connexion perdue' : `erreur ${status}` });
    }
  };

  if (sending.state === 'sending') {
    const pct = sending.total ? Math.round((sending.loaded / sending.total) * 100) : 0;
    return (
      <div className="grid justify-items-center gap-2.5 px-4 py-8 text-center">
        <Loader2 size={22} className="animate-spin text-[var(--accent)]" />
        <b>Envoi de votre demande…</b>
        <div className="h-1.5 w-[220px] overflow-hidden rounded-full bg-[var(--ink-100)]">
          <div className="h-full bg-[var(--accent)] transition-[width]" style={{ width: `${pct}%` }} />
        </div>
        {sending.what && sending.total > 0 && (
          <span className="text-xs text-[var(--ink-500)]">{sending.what} · {formatBytes(sending.loaded)} sur {formatBytes(sending.total)}</span>
        )}
      </div>
    );
  }

  if (sending.state === 'sent') {
    return (
      <>
        <div className="grid justify-items-center gap-2 px-4 pb-2 pt-6 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-green-100 text-green-700"><Check size={22} /></span>
          <b className="text-[15px]">Demande n° {sending.number} envoyée</b>
          <span className="max-w-[290px] text-xs leading-relaxed text-[var(--ink-500)]">
            Merci ! Nous vous répondrons par mail, à <b>{email}</b>. Vous retrouverez la réponse ici, dans « Mes demandes ».
          </span>
        </div>
        <footer className="flex items-center gap-2 border-t border-[var(--ink-100)] px-4 py-3">
          <button type="button" onClick={() => { setSending({ state: 'idle' }); open('list', sending.number); }} className={GHOST}>Voir ma demande</button>
          <span className="flex-1" />
          <button type="button" onClick={close} className={PRIMARY}>Fermer</button>
        </footer>
      </>
    );
  }

  if (details) {
    return (
      <>
        <AttachedContext {...details} attachCopy={draft.attachCopy}
          onToggleCopy={attach => setDraft(d => ({ ...d, attachCopy: attach }))} onBack={() => setDetails(null)} />
        <footer className="flex items-center gap-2 border-t border-[var(--ink-100)] px-4 py-3">
          <span className="flex-1" />
          <button type="button" onClick={() => setDetails(null)} className={PRIMARY}>Compris</button>
        </footer>
      </>
    );
  }

  const kind = draft.kind;
  return (
    <>
      <div className="grid min-h-0 flex-1 content-start gap-3 overflow-auto px-4 py-3">
        <div>
          <span className="mb-1.5 block text-[11.5px] font-bold text-[var(--ink-700)]">Vous nous écrivez pour…</span>
          <div className="grid grid-cols-3 gap-1.5">
            {KINDS.map(({ kind: k, label, icon: Icon }) => {
              const on = k === kind;
              const tone = on ? (k === 'BUG' ? 'border-[var(--danger)] bg-rose-50 text-rose-700' : 'border-[var(--accent)] bg-[var(--accent-50)] text-[var(--accent-700)]') : 'border-[var(--ink-200)] text-[var(--ink-600)] hover:bg-[var(--ink-50)]';
              return (
                <button key={k} type="button" aria-pressed={on} onClick={() => setDraft({ ...draft, kind: k })}
                  className={`grid justify-items-center gap-1 rounded-[10px] border px-1.5 py-2 text-xs font-semibold ${tone}`}>
                  <Icon size={18} />{label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label htmlFor="support-body" className="mb-1.5 block text-[11.5px] font-bold text-[var(--ink-700)]">{BODY_LABEL[kind]}</label>
          <textarea
            id="support-body"
            value={draft.body}
            placeholder={PLACEHOLDER[kind]}
            onChange={e => { setDraft({ ...draft, body: e.target.value }); if (emptyError) setEmptyError(false); }}
            className={`min-h-[104px] w-full resize-y rounded-[9px] border px-2.5 py-2 leading-snug outline-none focus:border-[var(--accent)] focus:ring-[3px] focus:ring-[var(--accent-ring)] ${emptyError || tooLong ? 'border-[var(--danger)]' : 'border-[var(--ink-200)]'}`}
          />
          <div className="mt-1 flex justify-between text-[11px] text-[var(--ink-500)]">
            <span className={emptyError ? 'text-[var(--danger)]' : ''}>{emptyError ? EMPTY_BODY_MESSAGE : ''}</span>
            <span className={tooLong ? 'font-semibold text-[var(--danger)]' : ''}>{count(length)} / {count(SUPPORT_LIMITS.bodyMax)}</span>
          </div>
        </div>
        <ShotsField shots={draft.shots} onChange={shots => setDraft(d => ({ ...d, shots }))} onHide={setCapturing} />
        {isBug ? (
          <div className="rounded-[10px] border border-[var(--ink-200)] bg-[var(--ink-50)] p-3 text-xs leading-relaxed">
            <label className="flex gap-2.5">
              <input type="checkbox" className="mt-0.5" checked={draft.attachCopy && projectName !== null} disabled={projectName === null}
                onChange={e => setDraft(d => ({ ...d, attachCopy: e.target.checked }))} />
              <span>
                {projectName !== null
                  ? <><b>Joindre une copie du projet « {projectName} »</b> tel qu&apos;il est maintenant, avec les informations techniques de la page.</>
                  : <><b>Aucun projet ouvert</b> : seules les informations techniques de la page seront jointes.</>}
                {' '}Elles nous servent à reproduire le problème ; votre projet n&apos;est jamais modifié.{' '}
                <button type="button" onClick={showDetails} className="font-semibold text-[var(--accent-700)] hover:underline">Voir ce qui sera envoyé</button>
              </span>
            </label>
            {heavy && draft.attachCopy && (
              <p className="m-0 mt-2 text-amber-800">Le projet est lourd, à cause de son image de fond : l&apos;envoi peut prendre une minute.</p>
            )}
          </div>
        ) : (
          <p className="m-0 text-xs text-[var(--ink-500)]">Nous joignons seulement la page et le projet ouverts, pour situer votre message.</p>
        )}
        {draft.assistant && draft.assistant.length > 0 && (
          <details className="rounded-[10px] border border-[var(--ink-200)] bg-[var(--ink-50)] px-3 py-2.5 text-xs leading-relaxed">
            <summary className="flex cursor-pointer items-center gap-2">
              <b className="flex-1">La conversation avec l&apos;assistant</b>
              <span className="text-[var(--ink-500)]">{draft.assistant.length} messages</span>
              <button type="button" onClick={e => { e.preventDefault(); setDraft(d => ({ ...d, assistant: undefined })); }}
                className="font-semibold text-[var(--accent-700)] hover:underline">Ne pas la joindre</button>
            </summary>
            <ol className="m-0 mt-2 list-decimal space-y-0.5 pl-[18px] text-[11.5px] text-[var(--ink-600)]">
              {draft.assistant.map((m, i) => (
                <li key={i}>{m.role === 'user' ? 'Vous' : 'Assistant'} : « {m.text.length > 140 ? `${m.text.slice(0, 140)}…` : m.text} »</li>
              ))}
            </ol>
          </details>
        )}
        <p className="m-0 text-xs text-[var(--ink-500)]">Réponse envoyée à <b>{email}</b>.</p>
        {sending.state === 'tooLarge' && (
          <div role="alert" className="flex gap-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-800">
            <AlertTriangle size={15} className="mt-0.5 flex-none" />
            <span><b>La copie du projet dépasse 50 Mo</b> : elle ne peut pas être jointe. Votre message et vos captures partiront sans elle.</span>
          </div>
        )}
        {(sending.state === 'failed' || sending.state === 'refused') && (
          <div role="alert" className="flex gap-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-relaxed text-rose-800">
            <AlertTriangle size={15} className="mt-0.5 flex-none" />
            {sending.state === 'failed'
              ? <span><b>L&apos;envoi a échoué</b> ({sending.reason}). Votre message et vos captures sont gardés : réessayez dans un instant.</span>
              : <span>{sending.message}</span>}
          </div>
        )}
      </div>
      <footer className="flex items-center gap-2 border-t border-[var(--ink-100)] px-4 py-3">
        <span className="flex-1" />
        <button type="button" onClick={close} className={GHOST}>Annuler</button>
        {sending.state === 'tooLarge' ? (
          <button type="button" onClick={() => send(true)} className={PRIMARY}><Send size={14} /> Envoyer sans la copie</button>
        ) : (
          <button type="button" onClick={() => send()} disabled={tooLong} className={PRIMARY}>
            {sending.state === 'failed' ? <><RotateCw size={14} /> Réessayer</> : <><Send size={14} /> Envoyer</>}
          </button>
        )}
      </footer>
    </>
  );
}
